import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSubjects, type Subject } from '../../api/subjects';
import { useEnumLabel, useEnumOptions, useEnumParent } from '../../api/enums';
import { useIsAdmin } from '../../api/me';
import { Button, ButtonGroup, ButtonLink, Input, Pagination, Select, Table } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { useAnnouncer } from '../../layout/Announcer';
import { useListParams } from '../../lib/useListParams';
import { QueryError } from '../common/QueryError';
import { Loading } from '../common/LoadingPage';

const FILTERS = ['q', 'type', 'region', 'district'] as const;

export function SubjectsListPage() {
  const { t } = useTranslation();
  const admin = useIsAdmin();
  const label = useEnumLabel();
  const typeOptions = useEnumOptions('institutionTypes');
  const regionOptions = useEnumOptions('regions');
  const regionOf = useEnumParent();
  const { announce } = useAnnouncer();
  const list = useListParams(FILTERS, 'name,asc');
  const [draft, setDraft] = useState(list.filters);
  // the districts of the selected region; all of them, grouped by region, until a region is selected
  const districtOptions = useEnumOptions('districts', draft.region || undefined);

  /** A district of another region is cleared when the region changes (announced). */
  const changeRegion = (region: string) => {
    const keep = !draft.district || !region || regionOf('districts', draft.district) === region;
    setDraft({ ...draft, region, district: keep ? draft.district : '' });
    if (!keep) announce(t('subjects.districtCleared', { district: label('districts', draft.district), region: label('regions', region) }));
  };
  const query = useSubjects({
    q: list.filters.q || undefined,
    type: list.filters.type || undefined,
    region: list.filters.region || undefined,
    district: list.filters.district || undefined,
    page: list.page,
    size: 20,
    sort: list.sort,
  });
  usePageTitle([t('subjects.title')]);

  const columnLabels: Record<string, string> = {
    name: t('subjects.fields.name'),
    eduid: t('subjects.fields.eduid'),
    type: t('subjects.fields.type'),
    municipality: t('subjects.fields.municipality'),
    district: t('subjects.fields.district'),
    region: t('subjects.fields.region'),
  };

  const apply = (event: FormEvent) => {
    event.preventDefault();
    list.update({ ...draft, page: null });
  };
  const clear = () => {
    const empty = { q: '', type: '', region: '', district: '' };
    setDraft(empty);
    list.update({ ...empty, page: null });
  };
  const onSort = (key: string) => {
    const direction = list.toggleSort(key);
    announce(t('table.sortedBy', { column: columnLabels[key], direction: t(`table.${direction}`) }));
  };

  return (
    <>
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          <PageHeading>{t('subjects.title')}</PageHeading>
        </div>
        {admin && (
          <div className="govuk-grid-column-one-third app-heading-actions">
            <ButtonLink to="/subjekty/novy">{t('subjects.add')}</ButtonLink>
          </div>
        )}
      </div>

      <form className="app-filters" role="search" aria-label={t('subjects.filters.label')} onSubmit={apply} noValidate>
        <h2 className="govuk-heading-s">{t('subjects.filters.heading')}</h2>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <Input id="filter-q" name="q" label={t('subjects.filters.q')} hint={t('subjects.filters.qHint')} value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} />
          </div>
          <div className="govuk-grid-column-one-half">
            <Select id="filter-type" name="type" label={t('subjects.fields.type')} options={typeOptions} placeholder={t('common.all')} value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} width="full" />
          </div>
        </div>
        <div className="govuk-grid-row">
          <div className="govuk-grid-column-one-half">
            <Select id="filter-region" name="region" label={t('subjects.fields.region')} options={regionOptions} placeholder={t('common.all')} value={draft.region} onChange={(e) => changeRegion(e.target.value)} width="full" />
          </div>
          <div className="govuk-grid-column-one-half">
            <Select id="filter-district" name="district" label={t('subjects.fields.district')} options={districtOptions} placeholder={t('common.all')} value={draft.district} onChange={(e) => setDraft({ ...draft, district: e.target.value })} width="full" />
          </div>
        </div>
        <ButtonGroup>
          <Button type="submit">{t('common.applyFilters')}</Button>
          <Button variant="secondary" onClick={clear}>
            {t('common.clearFilters')}
          </Button>
        </ButtonGroup>
      </form>

      {query.error ? (
        <QueryError error={query.error} />
      ) : query.isPending ? (
        <Loading />
      ) : (
        <>
          <Table<Subject>
            caption={t('subjects.tableCaption')}
            rows={query.data.content}
            rowKey={(s) => s.id}
            sort={list.sortState}
            onSort={onSort}
            columns={[
              {
                key: 'name',
                header: columnLabels.name,
                sortable: true,
                rowHeader: true,
                render: (s) => (
                  <Link className="govuk-link" to={`/subjekty/${s.id}`}>
                    {s.name}
                  </Link>
                ),
              },
              { key: 'eduid', header: columnLabels.eduid, sortable: true, render: (s) => s.eduid },
              { key: 'type', header: columnLabels.type, sortable: true, render: (s) => label('institutionTypes', s.type) },
              { key: 'municipality', header: columnLabels.municipality, sortable: true, render: (s) => s.address.municipality },
              { key: 'district', header: columnLabels.district, sortable: true, render: (s) => label('districts', s.district) },
              { key: 'region', header: columnLabels.region, sortable: true, render: (s) => label('regions', s.region) },
            ]}
            emptyText={t('subjects.empty')}
          />
          <Pagination page={query.data.page} totalPages={query.data.totalPages} totalElements={query.data.totalElements} onPage={(p) => list.update({ page: p })} />
        </>
      )}
    </>
  );
}
